<template>
  <div class="c-settings-editor">
    <div ref="jsonEditorContainer" class="h-[50vh] rounded-2"></div>
    <div class="flex items-center justify-end mt-3">
      <CButton v-if="isEditing" class="mr-2" @click="handleCancel">{{ t('canvasUI.common.cancel') }}</CButton>
      <CButton :type="isEditing ? 'primary' : undefined" @click="handleEdit">
        {{ isEditing ? t('canvasUI.common.save') : t('canvasUI.common.edit') }}
      </CButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, h } from 'vue'
import { useI18n } from 'vue-i18n'
import { isEqual } from 'lodash-es'

import CButton from '@lib/components/CButton/CButton.vue'
import useConfirmation from '@lib/composables/useConfirmation'
import useThemeVars from '@lib/composables/useThemeVars'
import { loadScript, loadStyle } from '@lib/util/dom'

const props = defineProps<{
  settings: any
  onUpdateSettings: (updatedSettings: any) => Promise<{ settings: Record<string, any> }>
}>()
const themeVars = useThemeVars()
const STYLE_ID = 'json-editor-custom-style'
let jsonEditor: any = null
const { t } = useI18n()
const confirmation = useConfirmation()
const jsonEditorContainer = ref<HTMLElement | null>(null)
const isEditing = ref(false)
const settingsDiff = ref('')

const switchMode = (mode: 'read' | 'write') => {
  if (mode === 'read') {
    isEditing.value = false
    jsonEditor.setMode('view')
    jsonEditor.expandAll()
  } else if (mode === 'write') {
    isEditing.value = true
    jsonEditor.setMode('code')
  }
}

const handleCancel = () => {
  jsonEditor.set(props.settings)
  switchMode('read')
}

const handleEdit = async () => {
  if (!jsonEditor) return
  if (isEditing.value) {
    let updatedSettings
    try {
      updatedSettings = jsonEditor.get()
    } catch (error) {
      alert(t('canvasUI.CSettingsEditor.invalidJsonAlert'))
      return
    }
    try {
      const originalSettings = props.settings
      settingsDiff.value = generateDiff(originalSettings, updatedSettings)

      if (isEqual(originalSettings, updatedSettings)) {
        switchMode('read')
        return
      }
      confirmation.warning({
        title: t('canvasUI.CSettingsEditor.title'),
        content: () =>
          h('pre', {
            innerHTML: settingsDiff.value,
            class: 'text-sm whitespace-pre-wrap max-h-[40vh] overflow-y-auto',
          }),
        confirmText: t('canvasUI.common.apply'),
        cancelText: t('canvasUI.common.cancel'),
        hasExit: false,
        disableCancelWhenLoading: true,
        onConfirm: async () => await confirmedEdit(updatedSettings),
        style: {
          width: '80%',
          maxWidth: '800px',
        },
      })
    } catch (error) {
      console.warn('Failed to update settings', error)
    }
  } else {
    switchMode('write')
  }
}

const confirmedEdit = async (updatedSettings: any) => {
  try {
    const updatedEntity = await props.onUpdateSettings(updatedSettings)
    jsonEditor.set(updatedEntity.settings)
    switchMode('read')
  } catch (error) {
    console.error('Failed to update settings', error)
  }
}

const generateDiff = (original: any, updated: any): string => {
  const diff = []
  original = original || {}
  updated = updated || {}
  // Check for updated and added properties
  for (const key in updated) {
    if (!Object.prototype.hasOwnProperty.call(original, key)) {
      diff.push(
        `${key}:\n<span style="color: ${themeVars.value.success};">${JSON.stringify(updated[key], null, 2)}</span> (Added)`,
      )
    } else if (JSON.stringify(original[key]) !== JSON.stringify(updated[key])) {
      diff.push(
        `${key}:\n<span style="color: ${themeVars.value.error};">${JSON.stringify(original[key], null, 2)}</span>\n=>\n<span style="color: ${themeVars.value.success};">${JSON.stringify(updated[key], null, 2)}</span>`,
      )
    }
  }
  // Check for deleted properties
  for (const key in original) {
    if (!Object.prototype.hasOwnProperty.call(updated, key)) {
      diff.push(
        `${key}:\n<span style="color: ${themeVars.value.error};">${JSON.stringify(original[key], null, 2)}</span> (Deleted)`,
      )
    }
  }
  return diff.join('\n\n')
}

onMounted(() => {
  loadStyle('https://cdnjs.cloudflare.com/ajax/libs/jsoneditor/10.0.3/jsoneditor.min.css')
  loadScript('https://cdnjs.cloudflare.com/ajax/libs/jsoneditor/10.0.3/jsoneditor.min.js', () => {
    if (!window.JSONEditor) {
      console.warn('JSONEditor not found')
      return
    }
    jsonEditor = new window.JSONEditor(jsonEditorContainer.value, {
      mode: 'view',
      modes: ['view', 'code'],
      navigationBar: false,
      statusBar: false,
      mainMenuBar: false,
    })
    jsonEditor.set(props.settings)
    jsonEditor.expandAll()

    const style = document.createElement('style')
    style.id = STYLE_ID
    style.textContent = `
      .jsoneditor {
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.03);
      }
      .jsoneditor,
      .jsoneditor-menu {
        border-color: transparent !important;
      }
      div.jsoneditor,
      .jsoneditor-menu {
        background-color: transparent !important;
      }
      .jsoneditor,
      .jsoneditor-menu,
      .jsoneditor-tree,
      .jsoneditor-tree div,
      .jsoneditor-menu > button,
      .jsoneditor-menu > div {
        font-family: 'Roboto', sans-serif !important;
      }
      .ace_editor,
      .jsoneditor,
      .jsoneditor-tree {
        border-radius: 8px;
      }
      .jsoneditor-menu a.jsoneditor-poweredBy {
        color: white !important;
      }
    `
    document.head.appendChild(style)
  })
})

onUnmounted(() => {
  // Clean up style and editor
  const addedStyle = document.getElementById(STYLE_ID)
  if (addedStyle) addedStyle.remove()
  jsonEditor?.destroy()
})
</script>
